/**
 * Ticket Training Evidence Display State helper (Batch 18-H2).
 *
 * Pure mapping from the G4 surface-contract `completionSummary` to the
 * evidence-badge display state rendered on the ticket-context
 * recommendation card.
 *
 * Trust contract (verbatim from the H1 plan):
 *   - Evidence badges come from `completionSummary`. Policy badges
 *     stay on the existing `getRankedPolicyBadges` row in the tab.
 *   - Evidence badges DO NOT change ranking. The G4 ranker already
 *     incorporated `badges.alreadyCompleted` / `verificationPending` /
 *     `verified` / `revoked` / `visibility.suppressCompleteDisplay`
 *     into ranking + visibility decisions. This helper is display
 *     only.
 *   - Manual submitted does NOT equal verified — distinct labels +
 *     variants.
 *   - Revoked suppresses stale completed display
 *     (`suppressCompleteDisplay: true`).
 *   - Label vocabulary mirrors G16 (`Confirmed by Manager` /
 *     `Verification pending` / `Verification revoked` /
 *     `Manual completion submitted`) but the input shape is the G4
 *     `TrainingRecommendationCompletionSummary`, not the G16
 *     `LearningRequirementEvidenceSummary`. The helper deliberately
 *     does NOT import the G16 helper to avoid cross-surface coupling.
 *
 * Status authority: the helper trusts `completionSummary.status`
 * as authoritative. G6 `deriveCompletionSummary` already applied the
 * precedence (revoked → verified → manual+manager-required → manual
 * → none). This helper re-deriving precedence would risk drift; it
 * maps the resolved status directly.
 */

import type { TrainingRecommendationCompletionSummary } from '@/types/trainingRecommendations';

export type TicketTrainingEvidenceBadgeVariant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'destructive';

export interface TicketTrainingEvidenceBadge {
  label: string;
  testId: string;
  variant: TicketTrainingEvidenceBadgeVariant;
}

export interface TicketTrainingEvidenceDisplayState {
  evidenceBadges: TicketTrainingEvidenceBadge[];
  suppressCompleteDisplay: boolean;
  hasEvidenceState: boolean;
}

const EMPTY_DISPLAY_STATE: TicketTrainingEvidenceDisplayState = {
  evidenceBadges: [],
  suppressCompleteDisplay: false,
  hasEvidenceState: false,
};

export function buildTicketTrainingEvidenceDisplayState(
  completionSummary?: TrainingRecommendationCompletionSummary | null,
): TicketTrainingEvidenceDisplayState {
  const status = completionSummary?.status;

  switch (status) {
    case 'verified':
      return {
        evidenceBadges: [
          {
            // OD-35 (2026-10-15): the row does not carry the manager's name, and "Verified" never appears without a
            // named verifier — a manager's sign-off is a confirmation.
            label: 'Confirmed by Manager',
            testId: 'ticket-training-evidence-badge-verified',
            variant: 'default',
          },
        ],
        suppressCompleteDisplay: false,
        hasEvidenceState: true,
      };
    case 'verification_pending':
      return {
        evidenceBadges: [
          {
            label: 'Verification pending',
            testId: 'ticket-training-evidence-badge-verification-pending',
            variant: 'secondary',
          },
        ],
        suppressCompleteDisplay: false,
        hasEvidenceState: true,
      };
    case 'revoked':
      return {
        evidenceBadges: [
          {
            label: 'Verification revoked',
            testId: 'ticket-training-evidence-badge-verification-revoked',
            variant: 'destructive',
          },
        ],
        suppressCompleteDisplay: true,
        hasEvidenceState: true,
      };
    case 'manual_submitted':
      return {
        evidenceBadges: [
          {
            label: 'Manual completion submitted',
            testId:
              'ticket-training-evidence-badge-manual-completion-submitted',
            variant: 'outline',
          },
        ],
        suppressCompleteDisplay: false,
        hasEvidenceState: true,
      };
    case 'none':
    case undefined:
    default:
      return EMPTY_DISPLAY_STATE;
  }
}
